import React from 'react';
import { Form, Alert, Spinner, Badge } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClock, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import { usePrazos } from 'hooks/usePrazo';
import { PrazoDto } from 'services/prazoService';

interface SeletorPrazosProps {
    prazosIds: string[];
    onPrazosChange: (prazosIds: string[]) => void;
    isInvalid?: boolean;
    errorMessage?: string;
    disabled?: boolean;
    className?: string;
}

const SeletorPrazos: React.FC<SeletorPrazosProps> = ({
    prazosIds,
    onPrazosChange,
    isInvalid = false,
    errorMessage,
    disabled = false,
    className = ""
}) => {
    const prazosQuery = usePrazos(false); // Só prazos ativos

    const handlePrazoToggle = (prazoId: string) => {
        if (disabled) return;

        const newPrazosIds = prazosIds.includes(prazoId)
            ? prazosIds.filter(id => id !== prazoId)
            : [...prazosIds, prazoId];

        onPrazosChange(newPrazosIds);
    };

    if (prazosQuery.isLoading) {
        return (
            <div className={`d-flex align-items-center ${className}`}>
                <Spinner size="sm" className="me-2" />
                <span className="text-body-secondary">Carregando prazos...</span>
            </div>
        );
    }

    if (prazosQuery.isError) {
        return (
            <Alert variant="danger" className={`border border-danger border-opacity-25 bg-danger-subtle ${className}`}>
                <div className="text-danger-emphasis">
                    <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                    Erro ao carregar prazos. Tente novamente.
                </div>
            </Alert>
        );
    }

    const prazos = prazosQuery.data || [];

    if (prazos.length === 0) {
        return (
            <Alert variant="warning" className={`border border-warning border-opacity-25 bg-warning-subtle ${className}`}>
                <div className="text-warning-emphasis">
                    <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                    Nenhum prazo ativo encontrado. Crie prazos primeiro em <strong>Cadastros → Prazos</strong>.
                </div>
            </Alert>
        );
    }

    // Ordenar prazos por parcela
    const prazosOrdenados = [...prazos].sort((a, b) => a.parcela - b.parcela);

    return (
        <div className={className}>
            <div className="mb-3">
                <Form.Label className="text-body-emphasis fw-semibold mb-2">
                    Prazos Disponíveis
                </Form.Label>
                <Form.Text className="text-body-secondary d-block">
                    Selecione os prazos que estarão disponíveis para esta forma de pagamento
                </Form.Text>
            </div>

            <div className={`row g-3 ${disabled ? 'opacity-75' : ''}`}>
                {prazosOrdenados.map((prazo) => {
                    const isSelected = prazosIds.includes(prazo.id);
                    return (
                        <div key={prazo.id} className="col-xl-3 col-lg-4 col-md-6 col-sm-6">
                            <Form.Check
                                type="checkbox"
                                id={`prazo-${prazo.id}`}
                                checked={isSelected}
                                onChange={() => handlePrazoToggle(prazo.id)}
                                disabled={disabled}
                                className="d-none"
                            />
                            <Form.Label
                                htmlFor={`prazo-${prazo.id}`}
                                className={`
                                    d-block border rounded-3 p-3 text-center mb-0 position-relative overflow-hidden
                                    ${isSelected
                                        ? 'border-primary border-2 bg-primary bg-opacity-10 text-primary'
                                        : 'border-2 border-opacity-25 bg-body text-body-emphasis'
                                    }
                                    ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'}
                                `}
                                style={{
                                    transition: 'all 0.3s ease',
                                    minHeight: '120px',
                                    boxShadow: isSelected
                                        ? '0 4px 12px rgba(var(--bs-primary-rgb), 0.15)'
                                        : '0 2px 4px rgba(0,0,0,0.05)',
                                    transform: isSelected ? 'translateY(-2px)' : 'translateY(0)',
                                }}
                                onMouseEnter={(e) => {
                                    if (!disabled && !isSelected) {
                                        e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.1)';
                                        e.currentTarget.style.transform = 'translateY(-1px)';
                                    }
                                }}
                                onMouseLeave={(e) => {
                                    if (!disabled && !isSelected) {
                                        e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.05)';
                                        e.currentTarget.style.transform = 'translateY(0)';
                                    }
                                }}
                            >
                                {/* Check indicator */}
                                {isSelected && (
                                    <div
                                        className="position-absolute top-0 end-0 bg-primary text-white d-flex align-items-center justify-content-center"
                                        style={{
                                            width: '24px',
                                            height: '24px',
                                            borderRadius: '0 0.375rem 0 0.5rem',
                                            fontSize: '0.75rem'
                                        }}
                                    >
                                        ✓
                                    </div>
                                )}

                                <div className="d-flex flex-column align-items-center justify-content-center h-100">
                                    <div className="d-flex align-items-center mb-3">
                                        <FontAwesomeIcon
                                            icon={faClock}
                                            className={`me-2 ${isSelected ? 'text-primary' : 'text-body-secondary'}`}
                                            size="lg"
                                        />
                                        <span className="fw-bold fs-5">{prazo.parcela}x</span>
                                    </div>

                                    <div className={`fw-medium text-center lh-sm ${isSelected ? 'text-primary' : 'text-body-secondary'}`} style={{ fontSize: '0.9rem' }}>
                                        {prazo.descricao}
                                    </div>

                                    {prazo.formasPagamentoCount > 0 && (
                                        <div className="mt-2">
                                            <Badge
                                                bg={isSelected ? 'primary' : 'secondary'}
                                                bg-opacity={isSelected ? undefined : '25'}
                                                text={isSelected ? 'white' : 'body-secondary'}
                                                className="border-0"
                                                style={{ fontSize: '0.7rem' }}
                                            >
                                                {prazo.formasPagamentoCount} forma{prazo.formasPagamentoCount !== 1 ? 's' : ''}
                                            </Badge>
                                        </div>
                                    )}
                                </div>
                            </Form.Label>
                        </div>
                    );
                })}
            </div>

            {prazosIds.length > 0 && (
                <div className="mt-4 p-3 bg-primary bg-opacity-10 border-start border-4 border-primary rounded-end">
                    <div className="d-flex align-items-center text-primary">
                        <FontAwesomeIcon icon={faClock} className="me-2" />
                        <span className="fw-medium">
                            {prazosIds.length} prazo{prazosIds.length !== 1 ? 's' : ''} selecionado{prazosIds.length !== 1 ? 's' : ''}
                        </span>
                    </div>
                </div>
            )}

            {isInvalid && errorMessage && (
                <div className="invalid-feedback d-block mt-2">
                    {errorMessage}
                </div>
            )}
        </div>
    );
};

export default SeletorPrazos;